import { kTimelineApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { ApiError } from '@/util/errors/api-error'
import { TimelineUpdateHistoryResponse, TimelineUpdateHistoryResponseSchema } from '../store/schemas'

/**
 * Fetches the full update history for an asset (newest-first).
 */
export async function getTimelineAssetUpdates(assetId: string): Promise<TimelineUpdateHistoryResponse> {
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kTimelineApiBasePath}/asset/${assetId}/updates`
    const response = await fetch(apiUrl, requestOptions)

    if (!response.ok) {
      const data = await response.json()
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`), {
          extra: {
            requestUrl: apiUrl,
            requestOptions: requestOptions,
            response: JSON.stringify(response),
            data: JSON.stringify(data),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`)
    }

    const data = await response.json()
    return TimelineUpdateHistoryResponseSchema.parse(data)
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
        })
      })
    }
    throw error
  }
}
